import { useState, useEffect } from 'react'

export default function Contact() {
  useEffect(() => {
    document.title = 'Kontakt - Kristensen Byg og Montage ApS'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Kontakt Kristensen Byg og Montage ApS for et uforpligtende tilbud på dit næste byggeprojekt. Ring på 28 55 89 25 eller send en mail.'
      )
    }
  }, [])

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error'

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes'
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig emailadresse'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse skal udfyldes'
    }
    return newErrors
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        setSubmitStatus('error')
      }
    } catch (err) {
      console.error('Submit error:', err)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Contact Section */}
      <div className="mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Info (5 cols) */}
          <div className="lg:col-span-5 space-y-8 px-4 sm:px-0">
            <div>
              <h3 className="text-2xl font-bold uppercase tracking-wider text-anthracite mb-6">
                Lad os tage en snak om dit næste projekt
              </h3>
              <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed">
                Uanset om du har en færdig tegning eller blot en god idé, hører jeg meget gerne fra dig. Jeg rådgiver og giver gerne et uforpligtende tilbud på din opgave.
              </p>
            </div>

            <div className="divide-y divide-warm-gray-200">
              <div className="flex items-start gap-4 py-6 first:pt-0 last:pb-0">
                <div className="text-brand-green shrink-0 pt-1">
                  <i className="fa-light fa-building text-xl" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-anthracite uppercase tracking-wider">Virksomhed</h4>
                  <p className="text-sm text-warm-gray-600 mt-1">Kristensen Byg og Montage ApS</p>
                </div>
              </div>

              <div className="flex items-start gap-4 py-6 first:pt-0 last:pb-0">
                <div className="text-brand-green shrink-0 pt-1">
                  <i className="fa-light fa-location-dot text-xl" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-anthracite uppercase tracking-wider">Adresse</h4>
                  <p className="text-sm text-warm-gray-600 mt-1">
                    Bygade 51E<br />
                    7173 Vonge
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 py-6 first:pt-0 last:pb-0">
                <div className="text-brand-green shrink-0 pt-1">
                  <i className="fa-light fa-phone text-xl" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-anthracite uppercase tracking-wider">Telefon</h4>
                  <p className="text-sm text-warm-gray-600 mt-1">
                    <a href="tel:+4528558925" className="hover:text-brand-green transition-colors whitespace-nowrap font-semibold">
                      28 55 89 25
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 py-6 first:pt-0 last:pb-0">
                <div className="text-brand-green shrink-0 pt-1">
                  <i className="fa-light fa-envelope text-xl" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-anthracite uppercase tracking-wider">E-mail</h4>
                  <p className="text-sm text-warm-gray-600 mt-1">
                    <a href="mailto:kontakt@k-bm.dk" className="hover:text-brand-green transition-colors whitespace-nowrap font-semibold">
                      kontakt@k-bm.dk
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Small Map Section */}
            <div className="overflow-hidden border-y sm:border border-warm-gray-200 shadow-sm sm:rounded-lg">
              <iframe
                title="Kristensen Byg og Montage ApS - Adresse"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2241.603058866175!2d9.4312523!3d55.895318!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464c1737e40b37f1%3A0x7d63259b3662217f!2sBygade%2051E%2C%207173%20Vonge!5e0!3m2!1sda!2sdk!4v1710000000000!5m2!1sda!2sdk"
                width="100%"
                height="220"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full grayscale hover:grayscale-0 transition-all duration-500 block m-0 p-0"
              ></iframe>
            </div>
          </div>

          {/* Right Column: Form (7 cols) */}
          <div className="lg:col-span-7 bg-white/95 backdrop-blur-md p-6 sm:p-8 rounded-none sm:rounded-lg border-y sm:border border-warm-gray-200 shadow-2xl">
            <h3 className="text-xl font-bold uppercase tracking-wider text-anthracite mb-2">Send mig en besked</h3>
            <p className="text-xs sm:text-sm text-warm-gray-600 mb-6">
              Udfyld formularen, så vender jeg tilbage til dig hurtigst muligt.
            </p>

            {submitStatus === 'success' && (
              <div className="mb-6 rounded bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 flex items-start gap-3">
                <i className="fa-light fa-circle-check text-xl text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-bold uppercase tracking-wider">Beskeden blev sendt!</h4>
                  <p className="text-sm mt-1">Tak for din henvendelse. Jeg vender tilbage til dig så hurtigt som muligt.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 rounded bg-red-50 border border-red-200 p-4 text-red-800 flex items-start gap-3">
                <i className="fa-light fa-circle-exclamation text-xl text-red-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-bold uppercase tracking-wider">Der skete en fejl</h4>
                  <p className="text-sm mt-1">Kunne ikke sende din besked. Prøv venligst igen eller ring til mig direkte.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot Field - absolutely hidden from humans */}
              <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
                <label htmlFor="website_url">Honeypot</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                  Navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full rounded bg-warm-gray-50 border px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors ${
                    errors.name ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200'
                  }`}
                  placeholder="Dit fulde navn"
                />
                {errors.name && <p className="mt-1 text-xs font-medium text-red-600">{errors.name}</p>}
              </div>

              {/* Phone and Email Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Phone Field */}
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full rounded bg-warm-gray-50 border px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors ${
                      errors.phone ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200'
                    }`}
                    placeholder="F.eks. 28558925"
                  />
                  {errors.phone && <p className="mt-1 text-xs font-medium text-red-600">{errors.phone}</p>}
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded bg-warm-gray-50 border px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors ${
                      errors.email ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200'
                    }`}
                    placeholder="din@email.dk"
                  />
                  {errors.email && <p className="mt-1 text-xs font-medium text-red-600">{errors.email}</p>}
                </div>
              </div>

              {/* Description Field */}
              <div>
                <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                  Beskrivelse <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleChange}
                  className={`w-full rounded bg-warm-gray-50 border px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors resize-none ${
                    errors.description ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200'
                  }`}
                  placeholder="Beskriv dit projekt..."
                />
                {errors.description && (
                  <p className="mt-1 text-xs font-medium text-red-600">{errors.description}</p>
                )}
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded bg-brand-green py-3 px-4 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin" />
                      Sender...
                    </>
                  ) : (
                    <>
                      send forespørgsel
                      <i className="fa-light fa-paper-plane" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

    </div>
  )
}
